import { useState, useEffect, useContext } from 'react';
import { LocalizationProvider, DatePicker, DateCalendar, ClearIcon, CalendarIcon } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18next';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ro';
import 'dayjs/locale/hu';
import { UserContext } from '../../components/shared/useUser';
import { getDateFormatByFranchise } from '../../utils/index';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

const localeMap = {
  'en-GB': 'en-gb',
  'de-DE': 'de',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'ro-RO': 'ro',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

type Language = keyof typeof localeMap;

const setDayjsLocale = (language: Language): string => {
  const dayjsLocale = localeMap[language] ?? 'en-gb';
  dayjs.locale(dayjsLocale);
  return dayjsLocale;
};

const setDayjsLocaleFromLocalStorage = (): string => {
  const storedLanguage = (localStorage.getItem('i18nextLng') ?? 'en-GB') as Language;
  const dayjsLocale = localeMap[storedLanguage] ?? 'en-gb';
  dayjs.locale(dayjsLocale);
  return dayjsLocale;
};

interface DatePickerLocalizedProps {
  label: string;
  value?: string | null;
  valueForDateCalendar?: Dayjs | null;
  disabled?: boolean,
  disableHighlightToday?: boolean,
  format?: string;
  useCalendar: boolean
  onChange?: (date: string | null) => void;
  onChangeDateCalendar?: (date: Dayjs | null) => void;
  shouldDisableDateCalendar?: (date: Dayjs) => boolean;
  shouldDisableDate?: (date: string) => boolean;
  translations?: {
    clearButtonLabel: string;
    todayButtonLabel: string;
    cancelButtonLabel: string;
    okButtonLabel: string;
    datePickerToolbarTitle?: string;
  };
  handleEnterKey?: (event: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent<HTMLButtonElement>) => void;
}

const DatePickerLocalized: React.FC<DatePickerLocalizedProps> = ({
  label,
  value,
  valueForDateCalendar,
  disabled,
  disableHighlightToday,
  useCalendar,
  onChange,
  shouldDisableDate,
  onChangeDateCalendar,
  shouldDisableDateCalendar,
  translations,
  handleEnterKey,
}) => {
  const [locale, setLocale] = useState<string>(setDayjsLocaleFromLocalStorage());
  const { selectedStore } = useContext(UserContext);
  const displayFormat = getDateFormatByFranchise(selectedStore?.franchiseName);

  const [openCalendar, setOpenCalendar] = useState(false);

  const { t } = useTranslation('common');

  useEffect(() => {
    const currentLanguage = i18n.language as Language;
    setDayjsLocale(currentLanguage);
    setLocale(localeMap[currentLanguage] ?? 'en-gb');
    const handleLanguageChange = (lng: Language) => {
      const newLocale = setDayjsLocale(lng);
      setLocale(newLocale);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const handleDateChange = (newValue: string | null) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleDateChangeDateCalendar = (newValue: Dayjs | null) => {
    if (onChangeDateCalendar) {
      onChangeDateCalendar(newValue);
    }
  };

  const handleClear = () => {
    handleDateChange('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}
      adapterLocale={locale}
      localeText={
        !useCalendar
          ? {
            clearButtonLabel: translations?.clearButtonLabel ?? t('calendar.clearButtonLabel'),
            todayButtonLabel: translations?.todayButtonLabel ?? t('calendar.todayButtonLabel'),
            cancelButtonLabel: translations?.cancelButtonLabel ?? t('calendar.cancelButtonLabel'),
            okButtonLabel: translations?.okButtonLabel ?? t('calendar.okButtonLabel'),
            datePickerToolbarTitle: translations?.datePickerToolbarTitle ?? t('calendar.title'),
          }
          : undefined
      }
    >
      {useCalendar ? (
        <DateCalendar
          value={valueForDateCalendar || (value ? dayjs(value) : null)}
          onChange={(newValue) => {
            handleDateChangeDateCalendar(newValue);
          }}
          sx={{
            maxWidth: '100%',
            '& .Mui-selected': {
              borderRadius: '4px',
              width: '40px',
              height: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            '& .MuiPickersDay-root': {
              fontWeight: 'bold',
            },
            '& .Mui-disabled': {
              fontWeight: 'normal',
              color: 'lightgrey',
            },
            '& .MuiYearCalendar-root': {
              width: 'auto',
            },
          }}
          shouldDisableDate={shouldDisableDateCalendar
            ? (date) => shouldDisableDateCalendar(dayjs(date))
            : undefined}
          disabled={disabled}
          disableHighlightToday={disableHighlightToday}
          showDaysOutsideCurrentMonth
        />
      ) : (
        <DatePicker
          label={label}
          value={value ? dayjs(value, 'YYYY-MM-DD') : null}
          onChange={(newValue) => {
            const formattedDate = newValue ? dayjs(newValue).format('YYYY-MM-DD') : '';
            handleDateChange(formattedDate);
          }}
          shouldDisableDate={(date) => {
            const dayjsDate = dayjs(date);
            const dateString = dayjsDate.format('YYYY-MM-DD');
            return shouldDisableDate ? shouldDisableDate(dateString) : false;
          }}
          format={displayFormat}
          sx={{ width: '100%' }}
          open={openCalendar}  // Control calendar visibility via state
          onOpen={() => setOpenCalendar(true)}  // Open the calendar
          onClose={() => setOpenCalendar(false)}  // Close the calendar
          slotProps={{
            textField: {
              error: false,
              onKeyDown: (e) => { handleEnterKey && handleEnterKey(e); },
              InputProps: {
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, marginRight: -4 }}>
                    {value && <IconButton sx={{ paddingRight: '5px', height: '20px', width: '20px', mr: 2 }}
                      onClick={handleClear}
                      size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>}
                    <IconButton sx={{ paddingLeft: '5px', height: '20px', width: '20px' }}
                      size="small"
                      onClick={() => setOpenCalendar(true)}
                      onKeyDown={(e) => {
                        handleEnterKey && handleEnterKey(e); // Handle Enter key for the calendar icon button
                        handleEnterKey && e.preventDefault(); // Prevent the default behavior when pressing Enter on the calendar icon button
                      }}
                    >
                      <CalendarIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ),
              },
            },
            field: {
              clearable: false,
              onClear: () => handleClear,
            },
            openPickerButton: {
              onKeyDown: (e) => {
                handleEnterKey && handleEnterKey(e); // Handle Enter key for the calendar icon button
                handleEnterKey && e.preventDefault(); // Prevent the default behavior when pressing Enter on the calendar icon button
              },
            },
          }}
        />
      )}
    </LocalizationProvider >
  );
};

export default DatePickerLocalized;
